import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Card, Grid, Input, message, Modal, Space, Table, Tabs, Tag} from 'antd';
import {DeleteOutlined, EyeOutlined, PlusOutlined, ReloadOutlined, SafetyOutlined} from '@ant-design/icons';
import {
    createAuthority,
    deleteAuthorityBatch,
    getAuthorities,
} from '../api/authorityApi';
import {getGroups} from '../api/groupApi';
import ManagementHeader from './ManagementHeader';
import {pickList} from '../api/http';
import useApiPermission from '../hooks/useApiPermission';
import PermissionButton from './common/PermissionButton';
import PermissionGate from './common/PermissionGate';
import AuthorityDetailDrawer from './AuthorityDetailDrawer';

const TYPE_COLOR = { MENU_GROUP: 'blue', API_GROUP: 'geekblue' };
const TYPE_LABEL = { MENU_GROUP: '메뉴그룹', API_GROUP: 'API그룹' };

const AuthorityManagement = () => {
  const screens = Grid.useBreakpoint();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [trgtGroups, setTrgtGroups] = useState([]);
  const [userGroupMap, setUserGroupMap] = useState({});
  const [trgtGroupMap, setTrgtGroupMap] = useState({});
  
  // 권한 상세 Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRecord, setDrawerRecord] = useState(null);
  const [drawerEditMode, setDrawerEditMode] = useState(false);

  // 목록 필터 상태
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [userGroupFilter, setUserGroupFilter] = useState('');
  const [trgtGroupFilter, setTrgtGroupFilter] = useState('');

  // 권한 훅
  const { canCall, loading: permLoading } = useApiPermission();
  const canRead = canCall(getAuthorities);
  const canCreate = canCall(createAuthority);
  const canDeleteMulti = canCall(deleteAuthorityBatch);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (permLoading) return;
      if (!canRead) { setData([]); message.warning('권한 목록 조회 권한이 없습니다.'); return; }
      const res = await getAuthorities();
      const list = pickList(res);
      setData(list);
    } catch (e) { message.error('권한 목록 조회 실패'); }
    finally { setLoading(false); }
  }, [permLoading, canRead]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const load = async () => {
      try {
        const resUG = await getGroups('USER_GROUP');
        const ugList = pickList(resUG);
        setUserGroups(ugList);
        setUserGroupMap(Object.fromEntries(ugList.map(g => [g.groupId, g.groupNm])));

        const [menuRes, apiRes] = await Promise.all([ getGroups('MENU_GROUP'), getGroups('API_GROUP') ]);
        const menuList = pickList(menuRes);
        const apiList = pickList(apiRes);
        const all = [...menuList, ...apiList];
        setTrgtGroups(all);
        setTrgtGroupMap(Object.fromEntries(all.map(g => [g.groupId, g.groupNm])));

      } catch (e) {
        setUserGroups([]); setUserGroupMap({}); setTrgtGroups([]); setTrgtGroupMap({});
      }
    };
    load();
  }, []);

  const openCreate = () => { 
    if (permLoading || !canCreate) { 
      message.warning('생성 권한이 없습니다.'); 
      return; 
    } 
    setDrawerRecord(null); 
    setDrawerEditMode(true); 
    setDrawerOpen(true); 
  };
  
  const openDetail = (record) => { 
    setDrawerRecord(record); 
    setDrawerEditMode(false); 
    setDrawerOpen(true); 
  };

  const handleBatchDelete = () => {
    if (!selectedKeys.length) { message.warning('삭제할 항목 선택'); return; }
    if (permLoading || !canDeleteMulti) { message.warning('일괄 삭제 권한이 없습니다.'); return; }
    Modal.confirm({
      title: '선택 삭제',
      content: `${selectedKeys.length}개 권한을 삭제하시겠습니까?`,
      okText: '삭제', okType: 'danger', cancelText: '취소',
      onOk: async () => { try { await deleteAuthorityBatch(selectedKeys); message.success('삭제 완료'); setSelectedKeys([]); fetchData(); } catch { message.error('일괄 삭제 실패'); } }
    });
  };

  const filteredData = useMemo(() => {
    const ugf = userGroupFilter.trim().toLowerCase();
    const tgf = trgtGroupFilter.trim().toLowerCase();
    
    let result = data;
    
    // 타입 필터 적용
    if (typeFilter !== 'ALL') {
      result = result.filter(a => a.trgtGroupType === typeFilter);
    }
    
    // 검색어 필터 적용
    if (ugf || tgf) {
      result = result.filter(a => {
        const userGroupName = (userGroupMap[a.userGroupId] || '').toLowerCase();
        const trgtGroupName = (trgtGroupMap[a.trgtGroupId] || '').toLowerCase();
        
        return (!ugf || userGroupName.includes(ugf)) && 
               (!tgf || trgtGroupName.includes(tgf));
      });
    }
    
    return result;
  }, [data, typeFilter, userGroupFilter, trgtGroupFilter, userGroupMap, trgtGroupMap]);

  const columns = [
    { 
      title: '대상유형', 
      dataIndex: 'trgtGroupType', 
      key: 'trgtGroupType', 
      width: 120,
      sorter: (a, b) => (a.trgtGroupType || '').localeCompare(b.trgtGroupType || ''),
      render: v => <Tag color={TYPE_COLOR[v]||'default'}>{TYPE_LABEL[v]||v}</Tag> 
    },
    { 
      title: '사용자그룹명', 
      dataIndex: 'userGroupId', 
      key: 'userGroupId', 
      width: 240, 
      ellipsis: true,
      sorter: (a, b) => (userGroupMap[a.userGroupId] || '').localeCompare(userGroupMap[b.userGroupId] || ''),
      render: v => userGroupMap[v] || '-'
    },
    { 
      title: '대상그룹명', 
      dataIndex: 'trgtGroupId', 
      key: 'trgtGroupId', 
      ellipsis: true,
      sorter: (a, b) => (trgtGroupMap[a.trgtGroupId] || '').localeCompare(trgtGroupMap[b.trgtGroupId] || ''),
      render: v => trgtGroupMap[v] || '-'
    },
    { 
      title: '작업', 
      key: 'actions', 
      width: 100, 
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r)}>
            상세
          </Button>
        </Space>
      ) 
    }
  ];

  const headerActions = (
    <Space wrap size={8} style={{ width: '100%' }}>
      <PermissionButton 
        type="primary" 
        icon={<PlusOutlined />} 
        onClick={openCreate} 
        require={createAuthority}
        size={screens.xs ? 'small' : 'middle'}
      >
        {screens.xs ? '생성' : '권한 생성'}
      </PermissionButton>
      <PermissionButton 
        danger 
        icon={<DeleteOutlined />} 
        disabled={!selectedKeys.length} 
        onClick={handleBatchDelete} 
        require={deleteAuthorityBatch}
        size={screens.xs ? 'small' : 'middle'}
      >
        선택삭제
      </PermissionButton>
      <PermissionButton 
        icon={<ReloadOutlined />} 
        onClick={fetchData} 
        require={getAuthorities}
        loading={loading}
        size={screens.xs ? 'small' : 'middle'}
      >
        새로고침
      </PermissionButton>
      <Input 
        placeholder={screens.xs ? '사용자' : '사용자 그룹'}
        allowClear 
        style={{ width: screens.xs ? 100 : 180 }} 
        value={userGroupFilter} 
        onChange={e => setUserGroupFilter(e.target.value)}
        size={screens.xs ? 'small' : 'middle'}
      />
      <Input 
        placeholder={screens.xs ? '대상' : '대상 그룹'}
        allowClear 
        style={{ width: screens.xs ? 100 : 180 }} 
        value={trgtGroupFilter} 
        onChange={e => setTrgtGroupFilter(e.target.value)}
        size={screens.xs ? 'small' : 'middle'}
      />
      <Button 
        onClick={() => { 
          setUserGroupFilter(''); 
          setTrgtGroupFilter(''); 
        }} 
        disabled={!userGroupFilter && !trgtGroupFilter}
        size={screens.xs ? 'small' : 'middle'}
      >
        {screens.xs ? '초기화' : '필터초기화'}
      </Button>
    </Space>
  );

  const tabItems = [
    {
      key: 'ALL',
      label: '전체',
      children: (
        <Table 
          size="small" 
          rowKey="authrtId" 
          columns={columns} 
          dataSource={filteredData} 
          loading={loading} 
          rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }} 
          pagination={{ pageSize: 15, showSizeChanger: false }} 
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({ 
            onClick: () => openDetail(record) 
          })}
        />
      )
    },
    {
      key: 'MENU_GROUP',
      label: '메뉴그룹',
      children: (
        <Table 
          size="small" 
          rowKey="authrtId" 
          columns={columns} 
          dataSource={filteredData} 
          loading={loading} 
          rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }} 
          pagination={{ pageSize: 15, showSizeChanger: false }} 
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({ 
            onClick: () => openDetail(record) 
          })}
        />
      )
    },
    {
      key: 'API_GROUP',
      label: 'API그룹',
      children: (
        <Table 
          size="small" 
          rowKey="authrtId" 
          columns={columns} 
          dataSource={filteredData} 
          loading={loading} 
          rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }} 
          pagination={{ pageSize: 15, showSizeChanger: false }} 
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({ 
            onClick: () => openDetail(record) 
          })}
        />
      )
    }
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      <ManagementHeader 
        icon={<SafetyOutlined />} 
        title="권한 관리" 
        actions={headerActions} 
        description="사용자그룹과 대상(메뉴/API)그룹 권한을 관리합니다." 
      />
      <PermissionGate require={getAuthorities}>
        {({allowed}) => (
          <Card 
            size="small" 
            styles={{ body: { padding: 0, overflowX: 'auto' } }} 
            style={{ opacity: allowed ? 1 : 0.5, pointerEvents: allowed ? 'auto' : 'none' }}
          >
            <Tabs
              activeKey={typeFilter}
              onChange={setTypeFilter}
              items={tabItems}
              size="small"
              style={{ padding: '0 16px' }}
            />
          </Card>
        )}
      </PermissionGate>
      
      <AuthorityDetailDrawer
        open={drawerOpen}
        record={drawerRecord}
        initialEditMode={drawerEditMode}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerRecord(null);
          setDrawerEditMode(false);
        }}
        onRegistered={() => fetchData()}
        onDelete={() => {
          fetchData();
          setSelectedKeys([]);
        }}
      />
    </div>
  );
};

export default AuthorityManagement;
